<script setup lang="ts">

import ConfigurationSectionRow from "@/pages/Settings/AppConfigurationPage/components/ConfigurationSectionRow.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import {useGlobalStore} from "@/store";
import {computed, onMounted, ref} from "vue";
import {isEqual} from "lodash";
import {isYapiError, YapiError} from "@YenzaCT/sdk";

const store = useGlobalStore();

const settings = ref({
  singleLogoutUrl: "",
  emailConfirmationUrl: ""
});

const changed = computed(
  () => !isEqual(store.appConfiguration.authSettings.sbOpenid, settings.value)
);

const confirmVisible = ref(false);

onMounted(() => {
  reset();
});

async function save() {
  try {
    const authSettings = store.appConfiguration.authSettings;
    authSettings.sbOpenid = settings.value;

    await store.saveAppConfiguration("authSettings", authSettings);
    store.showSnackbar("Saved policy configuration");
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    confirmVisible.value = false;
    reset();
  }
}

function reset() {
  if (store.appConfiguration.authSettings.sbOpenid?.singleLogoutUrl)
    settings.value.singleLogoutUrl = store.appConfiguration.authSettings.sbOpenid?.singleLogoutUrl;

  if (store.appConfiguration.authSettings.sbOpenid?.singleLogoutUrl)
    settings.value.emailConfirmationUrl = store.appConfiguration.authSettings.sbOpenid?.emailConfirmationUrl;
}

</script>

<template>
  <v-card flat color="grey-lighten-4">
    <v-card-title>{{ $t("components.SBOpenIDAuthConfigForm.title") }}</v-card-title>
    <v-divider class="my-2"/>
    <v-card-text>
      <configuration-section-row>
        <template #name>
          Single Sign  Out URL
        </template>
        <template #comment>
          Redirect URL for user on signout.
        </template>
        <v-text-field
          v-model="settings.singleLogoutUrl"
          color="primary"
          label="URL"
        />
      </configuration-section-row>
      <configuration-section-row>
        <template #name>
          Email Confirmation URL
        </template>
        <template #comment>
          URL user is sent to if they have not confirmed their account when using SB Auth.
        </template>
        <v-text-field
          v-model="settings.emailConfirmationUrl"
          color="primary"
          label="URL"
        />
      </configuration-section-row>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn
        @click="reset"
        color="secondary"
        :disabled="!changed"
      >
        {{ $t("buttons.reset") }}
      </v-btn>
      <v-btn
        @click="confirmVisible = true"
        color="primary"
        :disabled="!changed"
      >
        {{ $t("buttons.apply") }}
      </v-btn>
    </v-card-actions>
  </v-card>

  <confirm-dialog
    v-model="confirmVisible"
    @confirm="save"
    @cancel="confirmVisible = false"
    text="Are you sure you want to update the SB OpenID config?"
    title="SB OpenID"/>
</template>

<style scoped>

</style>
